import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteAlbumResult,
  DeleteAlbumOptions,
  PatchAlbumResult,
  PatchAlbumOptions,
  GetAlbumOptions,
  GetAlbumResult,
  getAlbum,
  DeleteAlbumFields,
  PatchAlbumFields,
  GetAlbumFields,
  deleteAlbum,
  patchAlbum,
} from '../fetchers/album';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetAlbumOptions<Fields extends GetAlbumFields> = GetAlbumOptions<Fields>;

/**
 * Get a specific showcase
 * This method returns the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbum<Fields extends GetAlbumFields>(
  getOptions: UseGetAlbumOptions<Fields> | (() => UseGetAlbumOptions<Fields> | null),
  config?: SWRConfiguration<GetAlbumResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/albums/${options.where.albumId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getAlbum({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbum, {
    endpoint: '/albums/:albumId',
    method: 'GET',
  });
}

/**
 * Get a specific showcase
 * This method returns the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumLazy<Fields extends GetAlbumFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetAlbumResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetAlbumOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}${serializeQuery(options)}`,
          getAlbum({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumLazy, {
    endpoint: '/albums/:albumId',
    method: 'GET',
  });
}

type UseDeleteAlbumOptions<Fields extends DeleteAlbumFields> = DeleteAlbumOptions<Fields>;

/**
 * Delete a showcase
 * This method deletes the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useDeleteAlbum<Fields extends DeleteAlbumFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteAlbumResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteAlbumOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}${serializeQuery(options)}`,
          deleteAlbum({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteAlbum, {
    endpoint: '/albums/:albumId',
    method: 'DELETE',
  });
}

type UsePatchAlbumOptions<Fields extends PatchAlbumFields> = PatchAlbumOptions<Fields>;

/**
 * Edit a showcase
 * This method edits the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function usePatchAlbum<Fields extends PatchAlbumFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchAlbumResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchAlbumOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}${serializeQuery(options)}`,
          patchAlbum({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchAlbum, {
    endpoint: '/albums/:albumId',
    method: 'PATCH',
  });
}
